import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  TablePagination,
  Switch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";
import PageHeader1 from "../../../components/common/PageHeader1";
import toast from "react-hot-toast";
import { getIntParam, getStringParam, setIfTruthy } from "../../../utils/urlState";

const getFullDate = (dateStr, timeStr) => {
  if (!dateStr) return null;
  if (timeStr) return new Date(`${dateStr.split("T")[0]}T${timeStr}`);
  return new Date(dateStr);
};

const formatDateWithTime = (dateObj) => {
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  const d = dateObj.getDate().toString().padStart(2, '0');
  const m = dateObj.toLocaleString('en-US', { month: 'short' });
  const y = dateObj.getFullYear();
  const time = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${d}-${m}-${y}, ${time}`;
};

const LoyaltyOfferList = () => {
  const token = localStorage.getItem("astroVani-admin-token");
  const [searchParams, setSearchParams] = useSearchParams();

  const [paginationModel, setPaginationModel] = useState({
    page: getIntParam(searchParams, "page", 0),
    pageSize: getIntParam(searchParams, "pageSize", 10),
  });

  const [searchQuery, setSearchQuery] = useState(getStringParam(searchParams, "search", ""));
  const [applyFilter, setApplyFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({});
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const didInitUrlSyncRef = React.useRef(false);
  const applySawLoadingRef = React.useRef(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  /* ================= FETCH LIST ================= */
  const fetchList = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: paginationModel.page + 1, // backend 1-based
        limit: paginationModel.pageSize,
      });

      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer?${params.toString()}`,
        { headers: { Authorization: token } }
      );

      setList(res.data?.data?.data || res.data?.data || []);
      setPagination(res.data?.paginationDetail || {});
    } catch {
      toast.error("Failed to fetch loyalty offers list");
    } finally {
      setLoading(false);
    }
  };


  /* 🔥 ONLY FILTER / PAGINATION CHANGE TRIGGERS API */
  useEffect(() => {
    fetchList();
  }, [paginationModel.page, paginationModel.pageSize, applyFilter]);

  // Apply button should stay disabled until request completes.
  useEffect(() => {
    if (!isApplyingFilters) return;
    if (loading) {
      applySawLoadingRef.current = true;
      return;
    }
    if (!loading && applySawLoadingRef.current) {
      applySawLoadingRef.current = false;
      setIsApplyingFilters(false);
    }
  }, [loading, isApplyingFilters]);

  // Keep URL in sync so Back restores pagination/filters.
  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }
    const params = new URLSearchParams();
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));
    setIfTruthy(params, "search", searchQuery);
    setSearchParams(params, { replace: true });
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, setSearchParams]);


  /* ================= FILTER & RESET ================= */
  const handleApplyFilter = () => {
    setIsApplyingFilters(true);
    applySawLoadingRef.current = false;
    setPaginationModel((p) => ({ ...p, page: 0 }));
    setApplyFilter((p) => !p);
  };

  const handleResetFilter = () => {
    setSearchQuery("");
    setPaginationModel({ page: 0, pageSize: 10 });
    setApplyFilter((p) => !p);
  };

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer/${selectedId}`,
        { headers: { Authorization: token } }
      );
      toast.success("Loyalty Offer deleted successfully");
      fetchList();
    } catch {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
      setOpen(false);
    }
  };

  /* ================= EXPORT ================= */
  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      const params = new URLSearchParams({ limit: 1000 });
      if (searchQuery) {
        params.append("search", searchQuery);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer?${params}`,
        { headers: { Authorization: token } }
      );

      const data = res.data?.data?.data || res.data?.data || [];

      const exportData = data.map((item, i) => ({
        "S.No": i + 1,
        "Title": item.title,
        "Start Date": formatDateWithTime(getFullDate(item.startDate, item.startTime)),
        "End Date": formatDateWithTime(getFullDate(item.endDate, item.endTime)),
        "Min Amount": item.minAmount,
        "Max Amount": item.maxAmount,
        "Gift %": item.giftPercentage,
        Status: item.isActive ? "Active" : "Inactive",
        "Created At": formatDateWithTime(new Date(item.createdAt)),
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Loyalty Offers");
      XLSX.writeFile(wb, "loyalty_offers_list.xlsx");

      toast.success("Exported successfully");
    } catch {
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  /* ================= TABLE ================= */
  const rows = useMemo(
    () =>
      list.map((item, index) => ({
        id: item._id,
        sono: paginationModel.page * paginationModel.pageSize + index + 1,
        title: item.title,
        startDate: getFullDate(item.startDate, item.startTime),
        endDate: getFullDate(item.endDate, item.endTime),
        minAmount: item.minAmount,
        maxAmount: item.maxAmount,
        giftPercentage: item.giftPercentage,
        status: item.isActive || false,
        createdAt: new Date(item.createdAt),
      })),
    [list, paginationModel]
  );

  const handleStatusChange = async (id, status) => {
    try {
      const isStatis = !status;
      setLoading(true);
      await axios.patch(
        `${process.env.REACT_APP_BASEURL}/admin/loyaltyOffer/toggle-status/${id}`,
        { isActive: isStatis },
        { headers: { Authorization: token } }
      );
      toast.success("Status updated successfully");
      fetchList();
    } catch {
      toast.error("Status update failed");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "sono", headerName: "S.No", width: 80 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: ({ row }) => (
        <>
          <IconButton component={Link} to={`/bucket-management-add?id=${row.id}`}>
            <i className="icofont-edit" />
          </IconButton>
          <IconButton onClick={() => { setSelectedId(row.id); setOpen(true); }}>
            <i className="icofont-ui-delete text-danger" />
          </IconButton>
        </>
      ),
    },
    { field: "title", headerName: "Title", width: 250 },
    { field: "startDate", headerName: "Start Date", width: 180, renderCell: ({ value }) => formatDateWithTime(value) },
    { field: "endDate", headerName: "End Date", width: 180, renderCell: ({ value }) => formatDateWithTime(value) },
    { field: "minAmount", headerName: "Min Amount", width: 120 },
    { field: "maxAmount", headerName: "Max Amount", width: 120 },
    { field: "giftPercentage", headerName: "Gift %", width: 100 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: ({ row }) => (
        <Switch
          checked={row.status}
          onChange={() => handleStatusChange(row.id, row.status)}
        />
      ),
    },
    { field: "createdAt", headerName: "Created At", width: 180, renderCell: ({ value }) => formatDateWithTime(value) },
  ];

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1
          pagetitle="Bucket Management List"
          righttitle="Add Bucket"
          link="/bucket-management-add"
          routebutton
        />

        {/* FILTER BAR */}
        <div className="card mb-3">
          <div className="card-body">
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                label="Search by title"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: { xs: "1 1 100%", md: "auto" } }}
              />

              <Box sx={{ display: "flex", gap: 2, width: { xs: "100%", md: "auto" } }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApplyFilter}
                  disabled={loading || isApplyingFilters}
                >
                  {isApplyingFilters ? "Applying..." : "Filter"}
                </Button>
                <Button variant="outlined" fullWidth onClick={handleResetFilter}>
                  Reset
                </Button>
              </Box>

              <Button
                variant="contained"
                sx={{ ml: { md: "auto" }, width: { xs: "100%", md: "auto" } }}
                onClick={exportToExcel}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "EXPORT"}
              </Button>
            </Box>
          </div>
        </div>

        {/* TABLE */}
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          hideFooterPagination
          paginationMode="server"
        />

        <TablePagination
          component="div"
          count={pagination?.totalDocs || 0}
          page={paginationModel.page}
          onPageChange={(_, newPage) =>
            setPaginationModel((prev) => ({ ...prev, page: newPage }))
          }
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={(e) =>
            setPaginationModel({
              page: 0,
              pageSize: parseInt(e.target.value, 10),
            })
          }
          rowsPerPageOptions={[10, 25, 50]}
        />
      </div>

      {/* DELETE MODAL */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Delete Bucket</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this bucket offer?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LoyaltyOfferList;
